import type { ReactNode } from 'react';
import { Field } from '@/shared/ui/Field';
import { SearchInput } from '@/shared/ui/SearchInput';
import { useOcultarValores, formatarValorMonetario } from '@/shared/state/OcultarValoresContext';
import type { EtapaKanban } from '@/features/orcamentos/api/etapasKanban';
import { ETAPA_FECHADO } from '@/features/orcamentos/api/etapasKanban';
import {
  ROTULO_BASE_DATA,
  STATUS_EXCETO_CANCELADAS,
  STATUS_TODOS,
  type BaseData,
} from '@/features/relatorios/filtrosRelatorio';
import type { VisaoItens } from '@/features/relatorios/filtrosAtivos';
import './FiltrosAba.css';

/** Formatador de moeda que respeita o "ocultar valores". */
export function useFormatoMoeda(): (v: number) => string {
  const { ocultar } = useOcultarValores();
  return (v: number) => formatarValorMonetario(v, ocultar);
}

/**
 * Card de uma tabela do relatório: título + busca no cabeçalho e, logo
 * abaixo, a grade de filtros da aba (1 coluna no celular).
 */
export function SecaoTabela({
  titulo,
  busca,
  onBusca,
  placeholder,
  filtroExtra,
  acoesTitulo,
  children,
}: {
  titulo: string;
  busca: string;
  onBusca: (v: string) => void;
  placeholder: string;
  filtroExtra?: ReactNode;
  /** Controles ao lado do título (ex.: Detalhado/Ranking). */
  acoesTitulo?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="card__header card__header--com-busca">
        <span className="card__title">{titulo}</span>
        <div className="secao-tabela__filtros">
          {acoesTitulo}
          <SearchInput valor={busca} onChange={onBusca} placeholder={placeholder} />
        </div>
      </div>
      {filtroExtra && <div className="filtros-aba">{filtroExtra}</div>}
      <div className="card__body card__body--flush">{children}</div>
    </div>
  );
}

/** Grade de filtros solta (fora de uma SecaoTabela) — ex.: Visão geral. */
export function GradeFiltrosAba({ children }: { children: ReactNode }) {
  return <div className="filtros-aba filtros-aba--solta card">{children}</div>;
}

/**
 * Status da O.S. Os itens fixos vêm primeiro ("todas exceto canceladas",
 * "todos", "somente fechadas/pagas"), depois cada etapa do kanban.
 */
export function SelectStatusOS({
  valor,
  onChange,
  etapas,
  rotulo = 'Status da O.S.',
}: {
  valor: string;
  onChange: (v: string) => void;
  etapas: EtapaKanban[];
  rotulo?: string;
}) {
  return (
    <Field rotulo={rotulo}>
      {(id) => (
        <select id={id} className="input" value={valor} onChange={(e) => onChange(e.target.value)}>
          <option value={STATUS_EXCETO_CANCELADAS}>Todas exceto canceladas</option>
          <option value={STATUS_TODOS}>Todos os status (inclui canceladas)</option>
          <option value={ETAPA_FECHADO}>Somente fechadas/pagas</option>
          <optgroup label="Etapa">
            {etapas
              .filter((e) => e.id !== ETAPA_FECHADO)
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.rotulo}
                </option>
              ))}
          </optgroup>
        </select>
      )}
    </Field>
  );
}

export function SelectBaseData({
  valor,
  onChange,
}: {
  valor: BaseData;
  onChange: (v: BaseData) => void;
}) {
  return (
    <Field
      rotulo="Data base"
      dica={valor === 'fechamento' ? 'Só O.S. fechadas/pagas, pelo dia do fechamento.' : undefined}
    >
      {(id) => (
        <select
          id={id}
          className="input"
          value={valor}
          onChange={(e) => onChange(e.target.value as BaseData)}
        >
          <option value="abertura">{ROTULO_BASE_DATA.abertura}</option>
          <option value="fechamento">{ROTULO_BASE_DATA.fechamento}</option>
        </select>
      )}
    </Field>
  );
}

/** Select rotulado genérico da grade de filtros. */
export function SelectFiltro({
  rotulo,
  valor,
  onChange,
  children,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <Field rotulo={rotulo}>
      {(id) => (
        <select id={id} className="input" value={valor} onChange={(e) => onChange(e.target.value)}>
          {children}
        </select>
      )}
    </Field>
  );
}

export function CheckboxFiltro({
  rotulo,
  marcado,
  onChange,
}: {
  rotulo: string;
  marcado: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="filtro-check">
      <input type="checkbox" checked={marcado} onChange={(e) => onChange(e.target.checked)} />
      <span>{rotulo}</span>
    </label>
  );
}

export function AlternanciaVisao({
  valor,
  onChange,
}: {
  valor: VisaoItens;
  onChange: (v: VisaoItens) => void;
}) {
  const opcoes: Array<{ id: VisaoItens; rotulo: string }> = [
    { id: 'detalhado', rotulo: 'Detalhado' },
    { id: 'ranking', rotulo: 'Ranking' },
  ];
  return (
    <div className="segmented alternancia-visao" role="group" aria-label="Visão do relatório">
      {opcoes.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`segmented__item${valor === o.id ? ' segmented__item--ativo' : ''}`}
          aria-pressed={valor === o.id}
          onClick={() => onChange(o.id)}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}
